import { useMemo } from "react";

import { useSelector } from "react-redux";

import { appsDashboardsOptions, dashboardSections, strCode } from "../consts";
import { deviceUtil } from "../utils";

const deviceOptions = deviceUtil.getDeviceOptions();

const worldwideMarketOption = { id: 0, value: "All" };

const allStoresOption = { id: 0, value: "All locations" };

const allAppsOption = { id: 0, value: "All Apps" };

const platforms = {
  windows: "Windows",
  android: "Android",
  linux: "Linux",
  macOs: "macOs",
  ios: "IOS"
};

const platformOptions = [
  { id: 0, value: "All" },
  { id: platforms.windows, value: "Windows" },
  { id: platforms.android, value: "Android" },
  { id: platforms.linux, value: "Linux" },
  { id: platforms.macOs, value: "Mac OS" },
  { id: platforms.ios, value: "IOS" },
  { id: 1, value: "Others" }
];

const browserOptions = [
  { id: 0, value: "All", platforms: [0] },
  { id: "Chrome", value: "Chrome", platforms: [0] },
  { id: "Edge", value: "Edge", platforms: [0] },
  {
    id: "Safari",
    value: "Safari",
    platforms: [platforms.macOs, platforms.ios, 1]
  },
  {
    id: "Mozilla",
    value: "Mozilla",
    platforms: [0]
  },
  { id: "Opera", value: "Opera", platforms: [0] },
  { id: "Bluefy", value: "Bluefy", platforms: [platforms.ios, 1] },
  { id: 1, value: "Others", platforms: [0] }
];

const groupsOptions = [
  { id: 0, value: "All tabs" },
  { id: dashboardSections.OVERVIEW, value: strCode.OVERVIEW },
  { id: dashboardSections.FIRMWARE, value: strCode.FIRMWARE },
  { id: dashboardSections.DIAGNOSTIC, value: strCode.DIAGNOSTIC },
  { id: dashboardSections.SETTINGS, value: strCode.SETTINGS },
  { id: dashboardSections.BATTERY_HEALTH, value: strCode.BATTERY_HEALTH }
];

const connectivityOptions = [
  { id: 0, value: "All" },
  { id: "usb", value: "USB" },
  { id: "ble", value: "Bluetooth" },
  { id: "wifi", value: "Wi-Fi" }
];

const appsDashboardsOptionsKeys = {
  [dashboardSections.OVERVIEW]: "OVERVIEW",
  [dashboardSections.FIRMWARE]: "FIRMWARE",
  [dashboardSections.DIAGNOSTIC]: "DIAGNOSTIC",
  [dashboardSections.SETTINGS]: "SETTINGS",
  [dashboardSections.BATTERY_HEALTH]: "BATTERY_HEALTH"
};

const useDashboardFilterOptions = () => {
  const allMarkets = useSelector((store) => store.markets.markets);
  const allApps = useSelector((store) => store.apps.apps);
  const userAppTypes = useSelector((store) => store.configs.appTypesGroups);
  const selectedAppsTypes = useSelector(
    (store) => store.dashboardFilters.selectedFilters.selectedAppType
  );

  const locationOptions = useSelector(
    (store) => store.dashboardFilters.selectedFilters.locationOptions
  );

  const marketOptions = useMemo(() => {
    let options = [worldwideMarketOption];

    if (allMarkets?.length) {
      options = [
        ...options,
        ...allMarkets
          ?.filter((market) =>
            selectedAppsTypes?.some((appType) => market.activeAppTypes.includes(appType.id))
          )
          .map((market) => {
            return {
              id: market.market,
              value: market.market
            };
          })
      ];
    }

    if (options.length <= 2 && options.some((option) => option.id === 0)) {
      return options.filter((option) => option.id !== 0);
    }

    return options;
  }, [allMarkets, selectedAppsTypes]);

  const availableGroups = useMemo(() => {
    if (!allApps || !allApps?.length) {
      return groupsOptions;
    }

    const options = [
      ...(groupsOptions || []).filter((group) => {
        if (group.id === 0) {
          return true;
        }

        const groupDashboards = Object.values(
          appsDashboardsOptions[appsDashboardsOptionsKeys[group.id]]
        ).map((value) => value.id);

        return selectedAppsTypes?.some((app) => {
          const appFull = allApps?.find((a) => a.appType === app.id);

          return appFull?.availableDashboards.some(
            (dashboard) => groupDashboards.includes(dashboard.name) && dashboard.isSelected
          );
        });
      })
    ];

    if (options.length <= 2 && options.some((option) => option.id === 0)) {
      return options.filter((option) => option.id !== 0);
    }

    return options;
  }, [selectedAppsTypes, allApps]);

  const appTypesOptions = useMemo(() => {
    return [
      allAppsOption,
      ...userAppTypes
        ?.map((appType) => {
          return { id: appType.appType, value: appType.name };
        })
        ?.sort((firstApp, secondApp) => firstApp.id.localeCompare(secondApp.id))
    ];
  }, [userAppTypes]);

  return {
    deviceOptions,
    marketOptions,
    locationOptions,
    platformOptions,
    browserOptions,
    appTypesOptions,
    groupsOptions: availableGroups,
    connectivityOptions,
    worldwideMarketOption,
    allStoresOption
  };
};

export default useDashboardFilterOptions;
